import { useEffect, useRef } from 'react';

import { FlowChartProvider } from '@/components/diagrams/FlowChart';
import { FlowChartWrapper } from '@/components/diagrams/FlowChart/components/FlowChartWrapper';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import { CustomerThemeProvider } from '@/styles/themes/CustomerThemeProvider';
import { diagnostics } from '@/utils/diagnostics';

import { FamilyTreeContextContainer } from './components/FamilyTreeContextContainer';
import { FamilyTreeSidePanel } from './components/FamilyTreeSidePanel';
import { FamilyTreeProvider, useFamilyTreeContext } from './contexts';
import {
  FamilyTreeSlots,
  FamilyTreeSlotsProvider,
  useFamilyTreeSlots,
} from './contexts/familyTreeSlots.context';
import { FamilyTreeFlowChart } from './FamilyTreeFlowChart';
import { useGetHouseholdFamilyTreeQuery } from './graphql/FamilyTree.generated';
import { useAutoOpenUnlinkedPanel } from './hooks/useAutoOpenUnlinkedPanel';
import { useFamilyTreeWarnings } from './hooks/useFamilyTreeWarnings';
import { useRemoveHiddenIndividuals } from './hooks/useRemoveHiddenIndividuals';

export interface FamilyTreeProps {
  householdId: string;
  height: number;
  width: number;
  /** presentationMode is used to disable interactivity with the graph */
  presentationMode?: boolean;
  registerFamilyTreeReady?: (householdId: string) => void;
  /** Portal target ref for rendering unlinked individuals button in page header */
  unlinkedButtonPortalRef?: React.RefObject<HTMLDivElement>;
  /** When provided, clicking the Edit button opens the trowser instead of toggling inline edit mode */
  onEditClick?: () => void;
  /** Hide the fullscreen button (used inside the trowser) */
  hideFullscreenButton?: boolean;
  /**
   * Invoked after an edit is persisted from within the tree (relationship
   * change or the node-edit drawer closing). In presentation/design mode this
   * refetches the presentation data so the slide reflects the update.
   */
  onAfterEdit?: () => void;
  /**
   * Invoked when the node side panel opens/closes. Lets a presentation host
   * suppress the chart-level hover treatment while the panel is open so it
   * doesn't show over (or flash before) the panel.
   */
  onSidePanelOpenChange?: (isOpen: boolean) => void;
  /** Optional UI injected by a host (presentation): per-tile toolbar + edit drawer. */
  slots?: FamilyTreeSlots;
}

interface FamilyTreeInnerProps extends FamilyTreeProps {
  sidePanelStateRef: React.MutableRefObject<{
    selectedPersonId?: string;
    isSidePanelOpen: boolean;
    sidePanelVariant?: 'linked' | 'unlinked';
    sidePanelHistory?: string[];
  }>;
}

const FamilyTreeInner = ({
  householdId,
  height,
  width,
  sidePanelStateRef,
  unlinkedButtonPortalRef,
  onEditClick,
  hideFullscreenButton,
  onSidePanelOpenChange,
}: FamilyTreeInnerProps) => {
  const { state } = useFamilyTreeContext();
  const { nodeEditDrawer } = useFamilyTreeSlots();

  useFamilyTreeWarnings();
  useAutoOpenUnlinkedPanel();

  useEffect(() => {
    onSidePanelOpenChange?.(state.isSidePanelOpen);
  }, [onSidePanelOpenChange, state.isSidePanelOpen]);

  // Sync side panel state to ref so it persists across remounts
  sidePanelStateRef.current = {
    selectedPersonId: state.selectedPersonId,
    isSidePanelOpen: state.isSidePanelOpen,
    sidePanelVariant: state.sidePanelVariant,
    sidePanelHistory: state.sidePanelHistory,
  };

  const variant = state.presentationMode ? 'transparent' : 'outlined';

  const flowChartContent = (
    <FamilyTreeContextContainer height={height} width={width}>
      <FlowChartWrapper style={{ height }} variant={variant}>
        {state.familyTreeData && (
          <FamilyTreeFlowChart
            presentationMode={state.presentationMode}
            unlinkedButtonPortalRef={unlinkedButtonPortalRef}
            onEditClick={onEditClick}
            hideFullscreenButton={hideFullscreenButton}
          />
        )}
      </FlowChartWrapper>
      {state.isSidePanelOpen && !state.presentationMode && (
        <FamilyTreeSidePanel householdId={householdId} />
      )}
      {/* Keep the node-edit drawer mounted (in presentation mode) so its
          internal `isOpen` toggles, letting MUI play the slide-in/out
          transition; mounting it already-open would skip the animation. */}
      {state.presentationMode && nodeEditDrawer}
    </FamilyTreeContextContainer>
  );

  return (
    <>
      <LoadingOverlay open={!state.familyTreeData} updatingText="Loading..." />
      {state.presentationMode ? (
        flowChartContent
      ) : (
        <CustomerThemeProvider forceLuminaryTheme={!state.renderWhiteLabeled}>
          {flowChartContent}
        </CustomerThemeProvider>
      )}
    </>
  );
};

export const FamilyTree = ({
  householdId,
  presentationMode = false,
  registerFamilyTreeReady,
  unlinkedButtonPortalRef,
  onAfterEdit,
  slots,
  ...props
}: FamilyTreeProps) => {
  const { showFeedback } = useFeedback();

  // Ref to preserve side panel state across remounts
  const sidePanelStateRef = useRef({
    selectedPersonId: undefined as string | undefined,
    isSidePanelOpen: false,
    sidePanelVariant: undefined as 'linked' | 'unlinked' | undefined,
    sidePanelHistory: undefined as string[] | undefined,
  });

  const { data } = useGetHouseholdFamilyTreeQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      householdId,
      clientProfileWhere: {
        hasHouseholdWith: [
          {
            id: householdId,
          },
        ],
      },
    },
    onError: (error) => {
      showFeedback(FeedbackMessages.queryError);
      diagnostics.error('family tree load failure', error, {
        householdId,
      });
    },
  });

  const { familyTreeData, clientProfiles } = useRemoveHiddenIndividuals(data);

  return (
    <FlowChartProvider>
      <FamilyTreeProvider
        householdId={householdId}
        familyTreeData={familyTreeData}
        clientProfiles={clientProfiles}
        initialSidePanelState={sidePanelStateRef.current}
        presentationMode={presentationMode}
        registerFamilyTreeReady={registerFamilyTreeReady}
        onAfterEdit={onAfterEdit}
      >
        <FamilyTreeSlotsProvider value={slots ?? {}}>
          <FamilyTreeInner
            householdId={householdId}
            sidePanelStateRef={sidePanelStateRef}
            unlinkedButtonPortalRef={unlinkedButtonPortalRef}
            {...props}
          />
        </FamilyTreeSlotsProvider>
      </FamilyTreeProvider>
    </FlowChartProvider>
  );
};
